import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Copy, WhatsappLogo, Users, Coins } from "@phosphor-icons/react";

/**
 * ReferralCard — Shows the user's referral code, total earnings, and list of referrals.
 * Also lets users (who didn't apply one at register) claim a referral code retroactively.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/referrals/me");
      setData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar tu código de referido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (!data?.code) return;
    navigator.clipboard.writeText(data.code).then(
      () => toast.success("Código copiado al portapapeles"),
      () => toast.error("No se pudo copiar")
    );
  };

  const shareWhatsApp = () => {
    if (!data?.code) return;
    const text = encodeURIComponent(
      `🎾 ¡Únete a Disfruta y Diviértete! Usa mi código ${data.code} al registrarte y ambos recibimos +${data.reward_per_referral} DYD Moneda gratis 🪙\n\nDescárgala: https://disfrutaydiviertete.com`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const applyCodeNow = async () => {
    const code = applyCode.trim().toUpperCase();
    if (!code) { toast.error("Ingresa un código"); return; }
    setApplying(true);
    try {
      const r = await api.post("/referrals/apply", { code });
      toast.success(`¡+${r.data.dyd_awarded} DYD recibidos!`, {
        description: `Cortesía de ${r.data.referrer_name}`,
      });
      setApplyOpen(false);
      setApplyCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código no válido");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#141414] border border-white/10 rounded-lg p-5 animate-pulse">
        <div className="h-4 bg-white/10 rounded w-1/3 mb-3"></div>
        <div className="h-10 bg-white/10 rounded"></div>
      </div>
    );
  }

  if (!data) return null;

  const canApplyExisting = !data.was_referred_by_code;

  return (
    <div className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-[#CCFF00]/30 rounded-lg p-5" data-testid="referral-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Gift size={20} weight="bold" className="text-[#CCFF00]" />
          <h3 className="font-['Barlow_Condensed'] text-xl uppercase tracking-wider text-white">Programa de Referidos</h3>
        </div>
        <Badge className="bg-[#CCFF00] text-black font-bold">+{data.reward_per_referral} DYD c/u</Badge>
      </div>

      <p className="text-xs text-gray-400 mb-3 leading-relaxed">
        Comparte tu código. Cada amigo que se registre con él, ambos reciben <span className="text-[#CCFF00] font-bold">+{data.reward_per_referral} DYD Moneda</span>.
      </p>

      {/* Code display */}
      <div className="bg-black border border-[#CCFF00]/40 rounded p-3 mb-3" data-testid="referral-code-box">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Tu Código</div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-2xl font-mono font-bold text-[#CCFF00] tracking-wider select-all" data-testid="referral-code-value">{data.code}</div>
          <Button size="sm" onClick={copyCode} className="bg-white/10 hover:bg-white/20 text-white h-8 px-2" data-testid="referral-copy-btn">
            <Copy size={14} className="mr-1" /> Copiar
          </Button>
        </div>
      </div>

      <Button onClick={shareWhatsApp} className="w-full bg-[#25D366] hover:bg-[#1FB855] text-white font-bold mb-3" data-testid="referral-whatsapp-btn">
        <WhatsappLogo size={18} weight="bold" className="mr-2" /> Compartir por WhatsApp
      </Button>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-black/40 border border-white/10 rounded p-3 text-center" data-testid="referral-stat-count">
          <Users size={16} className="text-[#CCFF00] mx-auto mb-1" />
          <div className="text-2xl font-bold text-white">{data.total_referrals}</div>
          <div className="text-[10px] tracking-wider uppercase text-gray-500">Referidos</div>
        </div>
        <div className="bg-black/40 border border-white/10 rounded p-3 text-center" data-testid="referral-stat-dyd">
          <Coins size={16} className="text-[#CCFF00] mx-auto mb-1" />
          <div className="text-2xl font-bold text-[#CCFF00]">{data.total_dyd_earned}</div>
          <div className="text-[10px] tracking-wider uppercase text-gray-500">DYD Ganados</div>
        </div>
      </div>

      {/* List of referrals */}
      {data.referrals && data.referrals.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2">Tus Referidos</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {data.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs bg-black/30 rounded px-2 py-1.5" data-testid={`referral-item-${r.id}`}>
                <span className="text-white truncate flex-1">{r.referred_name || "Anónimo"}</span>
                <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">+{r.dyd_awarded} DYD</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply existing code (retroactive, one-time) */}
      {canApplyExisting && (
        <div className="mt-4 border-t border-white/10 pt-3">
          {!applyOpen ? (
            <button
              type="button"
              onClick={() => setApplyOpen(true)}
              className="w-full text-[10px] tracking-[0.2em] uppercase text-gray-400 hover:text-[#CCFF00] font-bold py-2 border border-dashed border-white/10 hover:border-[#CCFF00]/50 rounded transition-colors"
              data-testid="referral-apply-toggle"
            >
              ¿Te invitó un amigo? Aplica su código aquí
            </button>
          ) : (
            <div className="space-y-2">
              <Input
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                placeholder="REF-XXXXXX"
                className="bg-black border-[#CCFF00]/30 text-white font-mono tracking-wider"
                data-testid="referral-apply-input"
              />
              <div className="flex gap-2">
                <Button onClick={() => { setApplyOpen(false); setApplyCode(""); }} variant="outline" className="border-white/10 flex-1" disabled={applying}>Cancelar</Button>
                <Button onClick={applyCodeNow} disabled={applying || !applyCode.trim()} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold flex-1" data-testid="referral-apply-submit">
                  {applying ? "Aplicando..." : "Aplicar"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {data.was_referred_by_code && (
        <div className="mt-3 text-[10px] text-gray-500 text-center italic">
          Te uniste con el código {data.was_referred_by_code}
        </div>
      )}
    </div>
  );
}
